import { OutComeTypes, CompletionTypes } from './postScoreConstants';

const WALKOVER_OUTCOMES = new Set([
    OutComeTypes.WITHDREW,
    OutComeTypes.DEFAULTED,
    OutComeTypes.DOUBLEWALKOVER,
]);

const MAX_SETS = 3;

const isUnplayed = (value) => value == null || value === '';

// The request carries only one tiebreak number per set — the set loser's count.
// Assign it to `tiebreak` (match-loser's slot) when the match winner won this set,
// otherwise to `winnerTiebreak` (match winner lost the set, so they are the set-loser).
// The opposing tiebreak figure isn't in the request; getMatch fills it.
const applyTiebreaker = (setEntry, winner, loser, tiebreaker) => {
    if (winner >= loser) {
        setEntry.tiebreak = tiebreaker;
    } else {
        setEntry.winnerTiebreak = tiebreaker;
    }
};

const buildScoreFromRequest = (request) => {
    const score = {};
    for (let i = 1; i <= MAX_SETS; i += 1) {
        const winner = request[`winnerSet${i}`];
        const loser = request[`loserSet${i}`];
        if (!isUnplayed(winner) || !isUnplayed(loser)) {
            const setEntry = { winner, loser };
            const tiebreaker = request[`tiebreakerSet${i}`];
            if (tiebreaker != null) {
                applyTiebreaker(setEntry, winner, loser, tiebreaker);
            }
            score[i] = setEntry;
        }
    }
    return score;
};

const deriveCompletionType = (request) => {
    if (request.matchOutcome) {
        return WALKOVER_OUTCOMES.has(request.matchOutcome)
            ? CompletionTypes.WINNER_DID_NOT_START
            : CompletionTypes.WINNER_INCOMPLETE;
    }
    return request.isWinner ? CompletionTypes.WINNER_COMPLETED : null;
};

const deriveOutcome = (request) => {
    if (request.matchOutcome) {
        return request.matchOutcome;
    }
    return request.completed ? OutComeTypes.COMPLETED : null;
};

// EventProfileMatchCard's score renderer (`getScoresForPosition` →
// `getMatchWinnerPositions`) maps `result.players.winner1` (a playerId) back
// to position1 or position2 via `participatingMembers`. Without it, the card
// shows the loser's score on BOTH sides.
const buildPlayersFromRequest = (request, existingPlayers) => {
    const players = { ...(existingPlayers || {}) };
    if (request.winner1?.id != null) {
        players.winner1 = request.winner1.id;
    }
    if (request.winner2?.id != null) {
        players.winner2 = request.winner2.id;
    }
    return players;
};

/**
 * Build an optimistic getMatch-shaped payload from a postScore request body
 * plus the existing slice match. Dispatching this through
 * EVENT_PROFILE_GET_MATCH_SUCCESS lets the Schedule/Draws UI update without
 * waiting for the server round-trip; the real getMatch fetch reconciles
 * shortly after (filling resultId, position*, output match ids, and any
 * server normalization).
 *
 * Only fields the FE rendering reads are synthesized — score, completionType,
 * outcome, completed. Other MERGE_FIELDS_FROM_GET_MATCH entries (position*,
 * participatingMembers, output match ids, resultId) are intentionally omitted
 * so mergeMatchFields preserves the existing slice values until reconciliation.
 */
export const buildSyntheticMatchPayload = (request, existingMatch, roundId) => ({
    id: request.matchId ?? existingMatch?.id,
    roundId: roundId ?? existingMatch?.roundId ?? null,
    result: {
        ...(existingMatch?.result || {}),
        score: buildScoreFromRequest(request),
        outcome: deriveOutcome(request),
        completionType: deriveCompletionType(request),
        players: buildPlayersFromRequest(request, existingMatch?.result?.players),
    },
    completed: request.completed === true,
    outcome: deriveOutcome(request),
});

export const buildSyntheticClearPayload = (existingMatch, roundId) => ({
    id: existingMatch?.id,
    roundId: roundId ?? existingMatch?.roundId ?? null,
    result: null,
    completed: false,
    outcome: null,
});
